import { useState, useEffect, useMemo } from "react";
import toast from "react-hot-toast";
import { adminAPI } from "../services/admin.api";
import { useLanguage } from "../../../context/LanguageContext";

export interface SpecialtyOption {
  id: number;
  name: string;
}

export interface Specialty {
  id: number;
  name: string;
  created_at: string;
  options?: SpecialtyOption[];
}

export const useSpecialtiesManagement = () => {
  const { t } = useLanguage();
  const [specialties, setSpecialties] = useState<Specialty[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [editingSpecialty, setEditingSpecialty] = useState<Specialty | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [name, setName] = useState("");
  const [options, setOptions] = useState<string[]>([]);

  const load = async () => {
    try {
      setLoading(true);
      const data = await adminAPI.getSpecialties();
      setSpecialties(data.data ?? data ?? []);
    } catch {
      toast.error(t("toast.specialty.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() =>
    specialties.filter((s) => s.name.toLowerCase().includes(searchQuery.toLowerCase())),
    [specialties, searchQuery]
  );

  const stats = useMemo(() => ({
    total: specialties.length,
    results: filtered.length,
  }), [specialties, filtered]);

  const openCreate = () => { setEditingSpecialty(null); setName(""); setOptions([]); setShowModal(true); };
  const openEdit = (s: Specialty) => {
    setEditingSpecialty(s);
    setName(s.name);
    setOptions((s.options ?? []).map((o) => o.name));
    setShowModal(true);
  };
  const closeModal = () => setShowModal(false);

  const save = async () => {
    if (!name.trim()) { toast.error(t("toast.specialty.nameRequired")); return; }
    try {
      const payload = { name: name.trim(), options: options.filter((o) => o.trim()) };
      if (editingSpecialty) {
        await adminAPI.updateSpecialty(String(editingSpecialty.id), payload);
      } else {
        await adminAPI.createSpecialty(payload);
      }
      toast.success(editingSpecialty ? t("toast.specialty.updated") : t("toast.specialty.created"));
      closeModal();
      await load();
    } catch (error) {
      toast.error(t("toast.specialty.saveFailed", { error: error instanceof Error ? error.message : t("common.unknownError") }));
    }
  };

  const remove = async (id: number) => {
    try {
      await adminAPI.deleteSpecialty(String(id));
      toast.success(t("toast.specialty.deleted"));
      await load();
    } catch {
      toast.error(t("toast.specialty.deleteFailed"));
    }
  };

  return {
    loading, filtered, stats, searchQuery, setSearchQuery,
    showModal, editingSpecialty, name, setName, options, setOptions,
    openCreate, openEdit, closeModal, save, remove,
  };
};
